import { Slot } from '@radix-ui/react-slot';
import cn from 'classnames';
import { forwardRef } from 'react';

import { filterStyleProps } from '../../../utils';
import { useStyleProps } from '../../../utils/hooks';

import type { ForwardedRef, ReactElement } from 'react';

import type { BoxProps } from './box.types';

/**
 * The `Box` is a general purpose container.
 *
 * ### Usage
 *
 * ```tsx
 * import { Box } from '@bloomreach/react-banana-ui';
 *
 * export default function MyOrangeBox() {
 *   return <Box backgroundColor="">Write an example of component usage</Box>;
 * }
 * ```
 */
const Box = forwardRef((props: BoxProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    asChild,
    children,
    className,
    display = 'block',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const style = useStyleProps({ ...props, display });

  // Explicitly set box-sizing to border-box to avoid unexpected layout issues.

  style.boxSizing = 'border-box';

  const Root = asChild ? Slot : 'div';

  return (
    <Root {...filterStyleProps(rest)} className={cn('rbui-box', className)} ref={forwardedRef} style={style}>
      {children}
    </Root>
  );
});

Box.displayName = 'Box';

export default Box;
